import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

// GET /admin/quotations/all-proformas — EVERY quotation in EVERY state
// (draft through superseded), superseded revisions included, so the admin
// can see a quotation's full history. Admin-only — requireRole('admin') at
// the app.js mount; a sales session gets a clean 403. The screen
// (pages/AllProformasPage.jsx) filters / sorts / searches this list
// client-side, matching AllRequestsPage.
export function useAllProformas() {
  return useQuery({
    queryKey: ['admin', 'allProformas'],
    queryFn: () => apiClient.get('/admin/quotations/all-proformas'),
    select: (data) => data.quotations,
  });
}

// GET /admin/quotations/:id/document — one quotation's customer-facing
// document (the accessTokenService.toGuestQuotation shape) for the
// read-only viewer, plus org / ref / status / hasPdf / supersededBy. Works
// for every state; a superseded revision returns the frozen figures that
// were actually sent, not today's price. Admin-only server-side.
export function useProformaDocument(id) {
  return useQuery({
    queryKey: ['admin', 'proformaDocument', id],
    queryFn: () => apiClient.get(`/admin/quotations/${id}/document`),
    enabled: Boolean(id),
  });
}

// POST /admin/quotations/:id/unseal, cached for the "Quotations" read-only
// document view (components/SealSection.jsx, used from
// pages/AllProformasViewPage.jsx) — a DELIBERATELY separate hook from
// hooks/useApprovalDetail.js#useUnsealQuotation even though they hit the
// same endpoint: that one merges its response into
// ['admin','approvalDetail',id], this page reads
// ['admin','proformaDocument',id] instead, a differently-shaped response
// (`{ document, seal, requestId, ... }`, not the admin quotation-detail
// shape the other cache holds). Reusing that hook here would silently
// update the WRONG cache — the exact class of stale-UI bug already found
// and fixed once in this codebase (see PROGRESS.md's sealed-bid-tender
// entry) — so this one only invalidates, letting the next GET .../document
// rebuild the correct shape, rather than attempting a shape-mismatched
// optimistic merge.
export function useUnsealProformaDocument(id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/admin/quotations/${id}/unseal`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'proformaDocument', id] });
    },
  });
}

// POST /admin/quotations/:id/requote — admin re-quotes any EXPIRED
// quotation directly, no can_requote gate and no assignment check
// (quotationService.requoteQuotation only gates actorRole 'sales'; see
// backend/src/routes/adminQuotationRoutes.js's own comment on this route).
// Success returns `{ quotation: <the new revision, a different id> }`; the
// row this page is showing (id) becomes 'superseded' as a side effect, not
// the new revision itself — so, exactly like useUnsealProformaDocument
// above (same isolation reasoning: this page's cache key is
// ['admin','proformaDocument',id], never the sales/approval-screen
// equivalents' own requote caches), this only INVALIDATES that key rather
// than merging a response shaped for a different id into it. The refetch
// then shows this quotation as superseded, and DocumentBanner's existing
// supersededNote already links straight to the new revision — no separate
// "show the new revision" plumbing needed.
export function useRequoteProformaDocument(id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/admin/quotations/${id}/requote`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'proformaDocument', id] });
    },
  });
}

export default useAllProformas;
